import type { EnrichmentRunConfig, SearchDescriptorsConfig } from '../types/enrichment-run-config';

/** Config keys for the two descriptor steps — kept in step with the creation
 *  payload split (`enrichment-run-payload.ts`), where these live under
 *  `searchDescriptors` rather than in `contentAttributes`. */
const SEARCH_DESCRIPTORS_KEY = 'search_descriptors';
const PHRASE_DESCRIPTIONS_KEY = 'phrase_descriptions';

/**
 * Re-expands a persisted run config into the flat set of attribute keys the run
 * enriched, so the delivery panel can offer exactly those. Inverts the split the
 * creation flow made: `contentAttributes` plus the descriptor steps that were
 * turned on.
 *
 * Returns `undefined` when the config is absent (not chosen in this browser) or
 * empty — the delivery panel reads that as "run set unknown" and falls back to
 * the full taxonomy rather than showing an empty picker.
 */
export function eligibleKeysFromRunConfig(
  runConfig: EnrichmentRunConfig | null | undefined,
): string[] | undefined {
  if (!runConfig) return undefined;

  const keys = [...(runConfig.contentAttributes ?? [])];
  if (runConfig.searchDescriptors?.enabled) keys.push(SEARCH_DESCRIPTORS_KEY);
  if (runConfig.searchDescriptors?.phraseDescriptions) keys.push(PHRASE_DESCRIPTIONS_KEY);

  return keys.length > 0 ? keys : undefined;
}

/**
 * The enriched-attribute set as echoed on a job-status / batch-detail response,
 * where the config sits as flat, nullable top-level fields. Coerces those to an
 * {@link EnrichmentRunConfig} and defers to {@link eligibleKeysFromRunConfig}, so
 * the descriptor steps are added only when the run actually turned them on — a
 * `searchDescriptors.phraseDescriptions: false` echo never offers that pill.
 */
export function eligibleKeysFromEcho(
  echo:
    | {
        contentAttributes?: string[] | null;
        searchDescriptors?: SearchDescriptorsConfig | null;
      }
    | null
    | undefined,
): string[] | undefined {
  if (!echo) return undefined;
  return eligibleKeysFromRunConfig({
    ...(echo.contentAttributes ? { contentAttributes: echo.contentAttributes } : {}),
    ...(echo.searchDescriptors ? { searchDescriptors: echo.searchDescriptors } : {}),
  });
}
